import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Switch } from "../../ui/switch";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Sparkles, Loader2, Check, X, ListChecks, BookOpen, AlertCircle, RefreshCw,
} from "lucide-react";

/**
 * Bulk FAQ generator — retroactively adds AI-generated FAQ content to
 * existing Learn articles so they become eligible for Google FAQPage
 * rich-results.
 *
 * Workflow:
 *   1. Modal opens → fetches eligible candidates (default: only articles
 *      that don't yet have FAQ content).
 *   2. Admin selects which articles to process (or "select all").
 *   3. Admin picks a locale (en/hi/fr/ar).
 *   4. "Generate previews" loops sequentially through selected articles,
 *      calls /bulk-faq/preview/{id} for each, and renders the proposed
 *      FAQ inline so the admin can review.
 *   5. Admin clicks "Approve" or "Discard" per row.
 *   6. "Apply approved" loops through approved proposals and PUTs them
 *      to /bulk-faq/apply/{id}, then refreshes the parent ArticlesTab.
 *
 * Safety:
 *   - Server re-sanitises the FAQ on apply (banned-word + length caps).
 *   - `overwrite=false` toggle prevents accidental overwrites of admin-
 *     edited FAQ content.
 *   - Each preview can be regenerated independently if the first draft
 *     isn't right.
 */
export default function BulkFaqModal({ open, onClose, onApplied }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [proposals, setProposals] = useState({}); // {article_id: {proposed_faq, status, used_fallback}}
  const [locale, setLocale] = useState("en");
  const [includeWithFaq, setIncludeWithFaq] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [stage, setStage] = useState("select"); // 'select' | 'review' | 'done'
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const fetchCandidates = async (only_missing) => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/admin/learn/ai/bulk-faq/candidates?only_missing=${only_missing ? "true" : "false"}`
      );
      setCandidates(data.items || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load candidates");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setStage("select");
    setProposals({});
    setProgress({ done: 0, total: 0 });
    fetchCandidates(!includeWithFaq);
  }, [open, includeWithFaq]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  const generatePreviews = async () => {
    if (selected.size === 0) {
      return toast.error("Select at least one article");
    }
    const ids = Array.from(selected);
    setStage("review");
    setProgress({ done: 0, total: ids.length });
    setBusy(true);
    // Reset stale state when re-generating — otherwise a previous run's
    // approvedCount can show next to freshly-generated (unapproved)
    // proposals, which is confusing and can drop the new proposals on
    // Apply.
    const next = {};
    setProposals({});
    for (let i = 0; i < ids.length; i++) {
      const aid = ids[i];
      try {
        const { data } = await api.post(
          `/admin/learn/ai/bulk-faq/preview/${aid}?locale=${encodeURIComponent(locale)}`
        );
        next[aid] = {
          proposed_faq: data.proposed_faq || [],
          used_fallback: !!data.used_fallback,
          status: "pending",
          slug: data.slug,
          title: data.title,
        };
      } catch (e) {
        next[aid] = {
          proposed_faq: [],
          used_fallback: false,
          status: "error",
          error: formatApiErrorDetail(e?.response?.data?.detail) || "preview failed",
        };
      }
      setProposals({ ...next });
      setProgress({ done: i + 1, total: ids.length });
    }
    setBusy(false);
  };

  const regenerateOne = async (aid) => {
    setProposals((p) => ({ ...p, [aid]: { ...(p[aid] || {}), status: "regenerating" } }));
    try {
      const { data } = await api.post(
        `/admin/learn/ai/bulk-faq/preview/${aid}?locale=${encodeURIComponent(locale)}`
      );
      setProposals((p) => ({
        ...p,
        [aid]: {
          ...(p[aid] || {}),
          proposed_faq: data.proposed_faq || [],
          used_fallback: !!data.used_fallback,
          status: "pending",
        },
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "regeneration failed");
      setProposals((p) => ({ ...p, [aid]: { ...(p[aid] || {}), status: "pending" } }));
    }
  };

  const setRowStatus = (aid, status) => {
    setProposals((p) => ({ ...p, [aid]: { ...(p[aid] || {}), status } }));
  };

  const approvedCount = useMemo(
    () => Object.values(proposals).filter((p) => p.status === "approved").length,
    [proposals],
  );

  const applyApproved = async () => {
    const approvedIds = Object.entries(proposals)
      .filter(([, v]) => v.status === "approved")
      .map(([k]) => k);
    if (approvedIds.length === 0) {
      return toast.error("Approve at least one proposal first");
    }
    setBusy(true);
    setProgress({ done: 0, total: approvedIds.length });
    let saved = 0;
    let failed = 0;
    for (let i = 0; i < approvedIds.length; i++) {
      const aid = approvedIds[i];
      try {
        await api.put(`/admin/learn/ai/bulk-faq/apply/${aid}`, {
          faq: proposals[aid].proposed_faq,
          overwrite,
        });
        setRowStatus(aid, "applied");
        saved += 1;
      } catch (e) {
        setRowStatus(aid, "error");
        setProposals((p) => ({
          ...p,
          [aid]: { ...(p[aid] || {}), error: formatApiErrorDetail(e?.response?.data?.detail) || "save failed" },
        }));
        failed += 1;
      }
      setProgress({ done: i + 1, total: approvedIds.length });
    }
    setBusy(false);
    setStage("done");
    toast.success(`${saved} article${saved === 1 ? "" : "s"} updated${failed ? `, ${failed} failed` : ""}.`);
    onApplied?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="bulk-faq-modal">
        <DialogHeader>
          <DialogTitle>
            <BookOpen className="w-4 h-4 inline text-emerald-700 mr-1" /> Bulk FAQ generator
          </DialogTitle>
          <DialogDescription>
            Add AI-generated FAQ content to existing Learn articles so they become eligible for Google "People also ask" rich-results. All proposals are reviewable before save.
          </DialogDescription>
        </DialogHeader>

        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50/50 border border-emerald-100 p-3 text-xs">
          <div className="flex items-center gap-2">
            <Label className="text-emerald-900/80 m-0">Language</Label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="rounded-lg border border-emerald-200 bg-white text-xs px-2 py-1"
              data-testid="bulk-faq-locale"
              disabled={busy || stage !== "select"}
            >
              <option value="en">EN</option>
              <option value="hi">HI</option>
              <option value="fr">FR</option>
              <option value="ar">AR</option>
            </select>
          </div>
          {stage === "select" && (
            <div className="flex items-center gap-1.5">
              <Switch id="include-with-faq" checked={includeWithFaq} onCheckedChange={setIncludeWithFaq} data-testid="bulk-faq-include-with-faq" />
              <Label htmlFor="include-with-faq" className="text-emerald-900/80 m-0 cursor-pointer">Include articles that already have FAQ</Label>
            </div>
          )}
          {stage === "review" && (
            <div className="flex items-center gap-1.5">
              <Switch id="overwrite" checked={overwrite} onCheckedChange={setOverwrite} data-testid="bulk-faq-overwrite" />
              <Label htmlFor="overwrite" className="text-emerald-900/80 m-0 cursor-pointer">Overwrite existing FAQ on apply</Label>
            </div>
          )}
          {busy && progress.total > 0 && (
            <div className="ml-auto flex items-center gap-1.5 text-emerald-700">
              <Loader2 className="w-3 h-3 animate-spin" />
              {progress.done}/{progress.total}
            </div>
          )}
        </div>

        {/* STAGE 1 — select candidates */}
        {stage === "select" && (
          <>
            {busy ? (
              <div className="py-10 text-center text-emerald-700/70 text-sm" data-testid="bulk-faq-loading">
                <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading candidates…
              </div>
            ) : candidates.length === 0 ? (
              <div className="py-10 text-center text-emerald-900/55" data-testid="bulk-faq-empty">
                <Check className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                <p className="text-sm font-medium">All caught up</p>
                <p className="text-xs mt-1">
                  Every published article already has FAQ content. Toggle "Include articles that already have FAQ" to overwrite.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-emerald-900/70 px-1">
                  <span>{candidates.length} article{candidates.length === 1 ? "" : "s"} {includeWithFaq ? "(including existing FAQ)" : "without FAQ"}</span>
                  <button type="button" onClick={selectAll} className="font-semibold text-emerald-700 hover:underline" data-testid="bulk-faq-select-all">
                    {selected.size === candidates.length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="space-y-2 mt-2" data-testid="bulk-faq-candidate-list">
                  {candidates.map((a) => (
                    <label
                      key={a.id}
                      className="flex items-start gap-3 rounded-xl border border-emerald-100 hover:bg-emerald-50/40 transition p-3 cursor-pointer"
                      data-testid={`bulk-faq-candidate-${a.id}`}
                    >
                      <Checkbox
                        checked={selected.has(a.id)}
                        onCheckedChange={() => toggleSelected(a.id)}
                        className="mt-1"
                        data-testid={`bulk-faq-check-${a.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-emerald-950">{a.title}</span>
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700/65">/{a.slug}</span>
                          {a.current_faq_count > 0 ? (
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5">
                              {a.current_faq_count} existing FAQ
                            </span>
                          ) : null}
                        </div>
                        {a.hero_excerpt && (
                          <p className="text-[11px] text-emerald-900/55 mt-1 line-clamp-2">{a.hero_excerpt}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="bulk-faq-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={generatePreviews}
                    disabled={selected.size === 0 || busy}
                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="bulk-faq-generate-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Generate previews ({selected.size})
                  </Button>
                </DialogFooter>
              </>
            )}
          </>
        )}

        {/* STAGE 2 — review proposals */}
        {stage === "review" && (
          <>
            <div className="space-y-3 mt-2">
              {Object.entries(proposals).map(([aid, p]) => (
                <div
                  key={aid}
                  className={`rounded-xl border p-3 transition ${
                    p.status === "approved" ? "border-emerald-300 bg-emerald-50/50"
                    : p.status === "discarded" ? "border-rose-200 bg-rose-50/30 opacity-70"
                    : p.status === "applied" ? "border-emerald-400 bg-emerald-100/40"
                    : p.status === "error" ? "border-rose-300 bg-rose-50"
                    : "border-emerald-100 bg-white"
                  }`}
                  data-testid={`bulk-faq-proposal-${aid}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-emerald-950 truncate">{p.title}</div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700/60">/{p.slug}</div>
                    </div>
                    {p.used_fallback && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <AlertCircle className="w-2.5 h-2.5" /> fallback
                      </span>
                    )}
                  </div>
                  {p.status === "error" ? (
                    <p className="text-xs text-rose-700">{p.error || "preview failed"}</p>
                  ) : p.proposed_faq?.length > 0 ? (
                    <ul className="space-y-1.5 text-xs text-emerald-900/85 mb-2">
                      {p.proposed_faq.map((f, i) => (
                        <li key={i}>
                          <span className="font-semibold">Q:</span> {f.q}<br />
                          <span className="font-semibold">A:</span> {f.a}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-emerald-700/60">Generating…</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {p.status === "applied" ? (
                      <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" /> Saved
                      </span>
                    ) : p.status === "approved" ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setRowStatus(aid, "pending")} className="rounded-full text-emerald-700" data-testid={`bulk-faq-undo-${aid}`}>
                          <X className="w-3 h-3 mr-1" /> Unapprove
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => regenerateOne(aid)} className="rounded-full text-emerald-700">
                          <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                        </Button>
                      </>
                    ) : p.status === "discarded" ? (
                      <Button size="sm" variant="ghost" onClick={() => setRowStatus(aid, "pending")} className="rounded-full text-emerald-700">
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => setRowStatus(aid, "approved")} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`bulk-faq-approve-${aid}`}>
                          <Check className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => regenerateOne(aid)} disabled={p.status === "regenerating"} className="rounded-full" data-testid={`bulk-faq-regen-${aid}`}>
                          {p.status === "regenerating" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Regenerate
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRowStatus(aid, "discarded")} className="rounded-full text-rose-700" data-testid={`bulk-faq-discard-${aid}`}>
                          <X className="w-3 h-3 mr-1" /> Discard
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-3 gap-2">
              <Button variant="outline" onClick={() => setStage("select")} className="rounded-full" data-testid="bulk-faq-back">
                Back
              </Button>
              <Button
                onClick={applyApproved}
                disabled={approvedCount === 0 || busy}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="bulk-faq-apply-btn"
              >
                <ListChecks className="w-4 h-4 mr-1" /> Apply approved ({approvedCount})
              </Button>
            </DialogFooter>
          </>
        )}

        {/* STAGE 3 — done */}
        {stage === "done" && (
          <div className="text-center py-10" data-testid="bulk-faq-done">
            <Check className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
            <p className="text-emerald-950 font-medium">All set.</p>
            <p className="text-xs text-emerald-900/65 mt-1">Your back-catalogue is now FAQ-ready and eligible for Google "People also ask" rich-results.</p>
            <Button onClick={onClose} className="rounded-full mt-4" data-testid="bulk-faq-close">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
